import React, { useState } from 'react';
import { BrandColor } from '@styleseat/brand';
import { Box, BoxSizeOptions } from '@styleseat/ui';
import {
  Button,
  PortalOverlay,
  PortalOverlayHeader,
  PortalOverlayModal,
} from '../../../ui';
import { InlineSigninAndSignupContainer } from '../../../consumer/booking/BookingFlow/SignInAndSignUp/InlineSigninAndSignupContainer';
import { useIsMobile } from '../../../../hooks';
import { ListMyBusinessBanner } from './ListMyBusinessBanner';
import { LoginStateOptions } from '../../../consumer/booking/BookingFlow/SignInAndSignUp/InlineSigninAndSignup';
import { InlineLoginModalTitle } from './InlineLoginModalTitle';
import { InlineLoginModalHeader } from './InlineLoginModalHeader';
import { ArrowsChevronLeftIcon, StyleSeatTextLogo } from '../../../ui/Icons';
import { AuthOrigins } from '../../../../modules/user/constants';

type NewLoginExperienceProps = {
  onLoginSuccess: (userData: any) => Promise<void>;
  onSignupSuccess: (userData: any) => Promise<void>;
  onCloseModal: () => void;
  canDisplaySSO?: boolean;
  helperText?: string;
  canShowSubscriptionFeatures?: boolean;
};

export const InlineLoginModal: React.FCWithChildren<NewLoginExperienceProps> = ({
  onLoginSuccess,
  onSignupSuccess,
  onCloseModal,
  canDisplaySSO = false,
  helperText,
  canShowSubscriptionFeatures,
}) => {
  const isMobile = useIsMobile();

  const [modalState, setModalState] = useState<LoginStateOptions>(LoginStateOptions.Initial);

  const handleLoginSuccess = async (userData: any) => {
    await onLoginSuccess(userData);
  };

  const handleAccountCreation = async (success: boolean, userData?: any) => {
    if (success) {
      await onSignupSuccess(userData);
    }
  };

  let paddingBottom: BoxSizeOptions = 96;
  switch (modalState) {
    case LoginStateOptions.MFA:
      paddingBottom = 72;
      break;
    case LoginStateOptions.Login:
      paddingBottom = 84;
      break;
    case LoginStateOptions.SignUp:
      paddingBottom = 56;
      break;
    case LoginStateOptions.Initial:
    default:
      break;
  }

  const handleCloseModal = () => {
    onCloseModal();
  };

  const shouldShowListMyBusinessBanner = (modalState === LoginStateOptions.SignUp
    || modalState === LoginStateOptions.Initial) && canShowSubscriptionFeatures;

  return (
    <PortalOverlay
      name="new-login-signup-experience"
      onBackdropClick={handleCloseModal}
      animationStyle={isMobile ? 'slideUp' : null}
    >
      <PortalOverlayModal
        position="menu"
        borderRadiusTop={isMobile ? 32 : 8}
        borderRadiusBottom={isMobile ? 0 : 8}
        testID="login_signup_modal"
        color={BrandColor.WhiteWash}
      >
        <InlineSigninAndSignupContainer
          canDisplaySSO={canDisplaySSO}
          renderTitle={() => (
            <InlineLoginModalTitle helperText={helperText} modalState={modalState} />
          )}
          renderHeader={onNavigateBack => (
            <PortalOverlayHeader
              hideBottomBorder
              renderTitle={() => (
                <InlineLoginModalHeader
                  onCloseModal={handleCloseModal}
                  leftSide={(modalState === LoginStateOptions.Initial) ? (
                    <StyleSeatTextLogo height={32} />
                  ) : (
                    <Button appearance="none" onClick={onNavigateBack}>
                      <ArrowsChevronLeftIcon />
                    </Button>
                  )}
                />
              )}
            />
          )}
          renderFooter={() => shouldShowListMyBusinessBanner && (
            <Box borderRadiusBottom={isMobile ? 0 : 8}>
              <ListMyBusinessBanner
                origin={AuthOrigins.LoginPage}
                onClick={onCloseModal}
              />
            </Box>
          )}
          paddingTop={0}
          paddingBottom={paddingBottom}
          paddingX={24}
          useInlineMfa
          onAccountCreated={handleAccountCreation}
          onSignInSuccess={handleLoginSuccess}
          onChangeState={setModalState}
        />
      </PortalOverlayModal>
    </PortalOverlay>
  );
};
